import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';

import { BinOpModal } from './BinOpModal.jsx';
import { BinCreateModal } from './BinCreateModal.jsx';

export function BinsPage() {
  const { t } = useTranslation();
  const [warehouseId, setWarehouseId] = useState('');
  const [opOpen, setOpOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const warehouses = useQuery({ queryKey: ['inventory', 'warehouses'], queryFn: () => inventoryApi.listWarehouses().then((r) => r.items) });

  useEffect(() => {
    if (!warehouseId && warehouses.data?.length) setWarehouseId(warehouses.data[0].id);
  }, [warehouses.data, warehouseId]);

  const bins = useQuery({ queryKey: ['inventory', 'bins', warehouseId], queryFn: () => inventoryApi.listBins(warehouseId).then((r) => r.items), enabled: Boolean(warehouseId) });
  const contents = useQuery({ queryKey: ['inventory', 'bin-contents', warehouseId], queryFn: () => inventoryApi.binContents(warehouseId).then((r) => r.items), enabled: Boolean(warehouseId) });

  const rows = contents.data || [];
  const binCount = (bins.data || []).length;

  const columns = [
    { key: 'bin', label: t('bin.col.bin'), render: (r) => <span className="mono">{r.binCode}{r.binLabel ? ` · ${r.binLabel}` : ''}</span> },
    { key: 'product', label: t('bin.col.product'), render: (r) => `${r.sku} · ${r.productName}` },
    { key: 'quantity', label: t('bin.col.quantity'), align: 'right', render: (r) => r.quantity },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('bin.title')}</h1>
          <p className="muted">{t('bin.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="ghost" onClick={() => setCreateOpen(true)}>{t('bin.action.newBin')}</Button>
          <Button variant="primary" onClick={() => setOpOpen(true)} disabled={!warehouseId}>{t('bin.action.book')}</Button>
        </div>
      </header>

      <div className="card" style={{ maxWidth: 320 }}>
        <label className="field">
          <span className="field-label">{t('bin.field.warehouse')}</span>
          <select className="input" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            {(warehouses.data || []).map((w) => <option key={w.id} value={w.id}>{w.code} · {w.name}</option>)}
          </select>
        </label>
      </div>

      <div className="card-grid">
        <MetricCard label={t('bin.metric.bins')} value={String(binCount)} />
        <MetricCard label={t('bin.metric.occupied')} value={String(new Set(rows.map((r) => r.binId)).size)} />
        <MetricCard label={t('bin.metric.lines')} value={String(rows.length)} />
      </div>

      <Table columns={columns} rows={rows} isLoading={contents.isLoading} emptyMessage={t('bin.empty')} />

      <BinOpModal open={opOpen} onClose={() => setOpOpen(false)} warehouseId={warehouseId} bins={bins.data || []} />
      <BinCreateModal open={createOpen} onClose={() => setCreateOpen(false)} warehouseId={warehouseId} />
    </section>
  );
}
